/**
 * useCachedFetch — stale-while-revalidate cache for page list data.
 *
 * On mount: synchronously reads from sessionStorage (per-key) and
 * returns the cached value as initial state — the page renders
 * INSTANTLY with the last-known data, no spinner.
 *
 * After mount: fires the async fetcher in the background. When it
 * resolves, updates both React state and the sessionStorage cache.
 *
 * Result: clicking between sidebar tabs feels instant from the
 * second visit onward — the list paints from cache, then refreshes
 * silently. The user only sees a loading spinner on the very first
 * visit per session.
 *
 * Usage:
 *   const { data: bots, loading, refetch } = useCachedFetch(
 *     ['bots', user?.user_id],
 *     () => botService.getBots(),
 *     [],
 *   );
 *
 * Cache key: serialized via JSON.stringify(keyParts). Include any
 * scope you want — userId for per-user caches, tenantId for per-tenant,
 * any filter values that change what's fetched.
 *
 * Session-scoped: uses sessionStorage so logout (which `localStorage
 * .clear()`-equivalents) doesn't carry stale data into the next
 * session, and so two browser windows don't share cached results.
 * Different keys per scope avoid cross-user leakage even within
 * the same session.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

type Key = readonly (string | number | null | undefined)[];

function cacheKey(keyParts: Key): string {
  return 'fetch_cache:' + JSON.stringify(keyParts);
}

function readCache<T>(keyParts: Key): T | null {
  try {
    const raw = window.sessionStorage.getItem(cacheKey(keyParts));
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeCache<T>(keyParts: Key, value: T): void {
  try {
    window.sessionStorage.setItem(cacheKey(keyParts), JSON.stringify(value));
  } catch {
    // Quota exceeded / sandbox — non-fatal.
  }
}

export interface CachedFetchResult<T> {
  data: T | null;
  /** True only during the very first fetch when no cache existed.
   *  Subsequent navigations to the same key get `data` from cache and
   *  `loading=false` immediately, even while the background refresh
   *  is in flight. */
  loading: boolean;
  /** Last fetch error, if any. Cleared by a successful refetch. */
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useCachedFetch<T>(
  keyParts: Key,
  fetcher: () => Promise<T>,
  deps: React.DependencyList = [],
): CachedFetchResult<T> {
  const keyJson = JSON.stringify(keyParts);
  const [data, setData] = useState<T | null>(() => readCache<T>(keyParts));
  const [loading, setLoading] = useState<boolean>(() => readCache<T>(keyParts) === null);
  const [error, setError] = useState<Error | null>(null);
  // Capture latest fetcher in a ref so the refetch callback isn't
  // re-created on every render (it shouldn't change identity just
  // because the caller passes a new arrow function literal).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const refetch = useCallback(async () => {
    try {
      setError(null);
      const fresh = await fetcherRef.current();
      setData(fresh);
      writeCache(keyParts, fresh);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyJson]);

  useEffect(() => {
    let cancelled = false;
    // Re-read cache on key change (the key changed so the previous
    // `data` is for a different scope and shouldn't carry over).
    const cached = readCache<T>(keyParts);
    setData(cached);
    setLoading(cached === null);
    setError(null);

    (async () => {
      try {
        const fresh = await fetcherRef.current();
        if (cancelled) return;
        setData(fresh);
        writeCache(keyParts, fresh);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyJson, ...deps]);

  return { data, loading, error, refetch };
}

/**
 * Invalidate a cached entry (e.g. after a mutation). Pass the same
 * key parts the cache was written under.
 */
export function invalidateCachedFetch(keyParts: Key): void {
  try {
    window.sessionStorage.removeItem(cacheKey(keyParts));
  } catch {
    // ignore
  }
}
